import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Copy, CheckCircle, Loader2, QrCode } from 'lucide-react';
import { toast } from 'sonner';

const PAG_STATUS = {
  pendente: { label: 'Frete a pagar', class: 'bg-amber-100 text-amber-800' },
  informado: { label: 'Pix informado — em conferência', class: 'bg-blue-100 text-blue-800' },
  pago: { label: 'Frete pago', class: 'bg-green-100 text-green-800' },
};

export function PagamentoBadge({ pagamento, testId }) {
  if (!pagamento) return null;
  const s = PAG_STATUS[pagamento.status] || PAG_STATUS.pendente;
  return <Badge className={s.class} data-testid={testId}>{s.label} · R$ {Number(pagamento.valor).toFixed(2).replace('.', ',')}</Badge>;
}

export function PixPagamentoDialog({ solicitacao, open, onClose, onInformar }) {
  const [sending, setSending] = useState(false);
  const pg = solicitacao?.pagamento;
  if (!pg) return null;

  const copiar = async () => {
    try { await navigator.clipboard.writeText(pg.pix_copia_cola); toast.success('Código Pix copiado!'); }
    catch { toast.error('Não foi possível copiar. Selecione o código e copie manualmente.'); }
  };

  const informar = async () => {
    setSending(true);
    try { await onInformar(solicitacao.id); onClose(); } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="dialog-pix">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><QrCode className="w-5 h-5 text-emerald-600" /> Pagar frete com Pix</DialogTitle>
          <DialogDescription>Solicitação {solicitacao.numero} · {pg.descricao}</DialogDescription>
        </DialogHeader>
        <div className="text-center">
          <div className="text-3xl font-bold text-slate-900" data-testid="pix-valor">R$ {Number(pg.valor).toFixed(2).replace('.', ',')}</div>
          <p className="text-sm font-semibold text-slate-700 mt-1">Sterisul - Central de Esterilização</p>
          <p className="text-xs text-slate-500">Recebedor: Esteribel Central de Esterilização Ltda · CNPJ 48.565.324/0001-01</p>
        </div>
        {pg.status === 'pago' ? (
          <div className="flex flex-col items-center gap-2 py-6 text-green-700" data-testid="pix-pago">
            <CheckCircle className="w-12 h-12" />
            <span className="font-semibold">Pagamento confirmado. Obrigado!</span>
          </div>
        ) : (
          <>
            <div className="flex justify-center bg-white p-3 border rounded-lg" data-testid="pix-qrcode">
              <QRCodeSVG value={pg.pix_copia_cola} size={210} level="M" includeMargin />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-600 mb-1">Pix Copia e Cola</p>
              <div className="text-[11px] break-all bg-slate-50 border rounded p-2 font-mono text-slate-700 max-h-20 overflow-y-auto select-all" data-testid="pix-copia-cola">{pg.pix_copia_cola}</div>
            </div>
            <Button onClick={copiar} variant="outline" className="w-full" data-testid="btn-copiar-pix"><Copy className="w-4 h-4 mr-2" /> Copiar código Pix</Button>
            {pg.status === 'informado' ? (
              <p className="text-sm text-center text-blue-700" data-testid="pix-informado">Você já informou o pagamento. Estamos conferindo.</p>
            ) : (
              <Button onClick={informar} disabled={sending} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="btn-ja-paguei">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />} Já paguei
              </Button>
            )}
            <p className="text-[11px] text-slate-500 text-center">Abra o app do seu banco, escolha Pix → Ler QR Code ou Pix Copia e Cola.</p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
